import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { ArrowLeft, BookOpen, CheckCircle, Video, Users, Target, Edit, Loader2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

export default function Chapter1Homework() {
  const navigate = useNavigate();
  const chapterId = 1;
  
  // Fallback hardcoded items if no API data
  const fallbackItems = [
    { id: 'fallback-1', title: 'Complete exercises 2 and 3 above', assignment_type: 'exercise', description: 'Practice identifying observations vs evaluations' },
    { id: 'fallback-2', title: 'Read pages 63-70: "Nonviolent Communication Primer"', assignment_type: 'reading', description: 'Introduction to NVC concepts' },
    { id: 'fallback-3', title: 'Read page 71: "Compassionate Communication Suggestions"', assignment_type: 'reading', description: 'Practical tips for compassionate communication' },
    { id: 'fallback-4', title: 'Read pages 73-76: "Lost Love and the Drive Towards Wholeness"', assignment_type: 'reading', description: 'Understanding emotional patterns' },
    { id: 'fallback-5', title: 'Watch Part 1 of NVC basics video (00:00-47:16)', assignment_type: 'video', description: 'The Purpose of NVC and Expressing Observations and Feelings' },
    { id: 'fallback-6', title: 'Read Chapters 1-3 in "Nonviolent Communication: A Language of Life"', assignment_type: 'reading', description: 'Giving From the Heart, Communication That Blocks Compassion, Observing Without Evaluating' }
  ];

  const [homeworkItems, setHomeworkItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [completed, setCompleted] = useState(() => {
    const saved = localStorage.getItem(`chapter${chapterId}HomeworkCompleted`);
    return saved ? JSON.parse(saved) : [];
  });

  const assignmentTypeConfig = {
    reading: { icon: BookOpen, color: 'text-blue-400', bg: 'bg-blue-500/20' },
    video: { icon: Video, color: 'text-purple-400', bg: 'bg-purple-500/20' },
    partner: { icon: Users, color: 'text-green-400', bg: 'bg-green-500/20' },
    practice: { icon: Target, color: 'text-orange-400', bg: 'bg-orange-500/20' },
    exercise: { icon: Edit, color: 'text-pink-400', bg: 'bg-pink-500/20' }
  };

  useEffect(() => {
    fetchHomework();
  }, []);

  const fetchHomework = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/homework/${chapterId}`);
      if (response.ok) {
        const data = await response.json();
        if (data.assignments && data.assignments.length > 0) {
          setHomeworkItems(data.assignments);
        } else {
          // Use fallback if no assignments from API
          setHomeworkItems(fallbackItems);
        }
      } else {
        setHomeworkItems(fallbackItems);
      }
    } catch (error) {
      console.error('Failed to fetch homework:', error);
      setHomeworkItems(fallbackItems);
    } finally {
      setLoading(false);
    }
  };

  const toggleComplete = (id) => {
    setCompleted(prev => {
      const newCompleted = prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id];
      localStorage.setItem(`chapter${chapterId}HomeworkCompleted`, JSON.stringify(newCompleted));
      if (!prev.includes(id)) {
        toast.success('Assignment completed! Great work!');
      }
      return newCompleted;
    });
  };

  const progress = homeworkItems.length > 0 ? Math.round((completed.length / homeworkItems.length) * 100) : 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Decorative background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 right-10 w-64 h-64 bg-green-500/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-amber-500/5 rounded-full blur-3xl"></div>
      </div>

      <div className="border-b border-slate-700/50 bg-slate-900/90 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button onClick={() => navigate('/belonging-ebook')} variant="outline" size="sm" className="border-slate-600 hover:border-slate-500 text-white hover:bg-slate-800">
                <ArrowLeft className="h-4 w-4 mr-2" />Back to Guide
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-white">Chapter 1: Home Practice</h1>
              </div>
            </div>
            <div className="flex items-center gap-2 text-slate-400 text-sm"><BookOpen className="h-4 w-4" /><span>Page 93</span></div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 py-12 relative z-10">
        <div className="max-w-4xl mx-auto">
          {/* Header with Progress */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-500/10 border border-green-500/20 rounded-full text-green-300 text-sm mb-6">
              <Sparkles className="h-4 w-4" />
              <span>Interactive Homework Tracker</span>
            </div>
            <CheckCircle className="h-16 w-16 text-green-400 mx-auto mb-4" />
            <h2 className="text-3xl font-bold text-white mb-4">Chapter 1 Homework Assignments</h2>
            <div className="bg-slate-800/40 border border-slate-700/50 rounded-full px-6 py-2 inline-block">
              <p className="text-white font-semibold">{completed.length} of {homeworkItems.length} completed ({progress}%)</p>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="bg-slate-800/40 border border-slate-700/50 rounded-lg p-2 mb-8">
            <div className="bg-gradient-to-r from-green-500 to-emerald-500 h-3 rounded-full transition-all duration-500" style={{ width: `${progress}%` }}></div>
          </div>

          {/* Admin Notice */}
          <div className="bg-amber-900/20 border border-amber-500/30 rounded-lg p-4 mb-8 text-center">
            <p className="text-amber-200 text-sm">
              ✨ These assignments are managed by your instructor via the Admin panel
            </p>
          </div>

          {/* Loading State */}
          {loading ? (
            <div className="text-center py-12">
              <Loader2 className="h-8 w-8 text-green-400 mx-auto animate-spin mb-4" />
              <p className="text-slate-400">Loading assignments...</p>
            </div>
          ) : (
            <div className="space-y-4">
              {homeworkItems.map((item) => {
                const typeConfig = assignmentTypeConfig[item.assignment_type] || assignmentTypeConfig.reading;
                const IconComponent = typeConfig.icon;
                const isCompleted = completed.includes(item.id);

                return (
                  <div 
                    key={item.id} 
                    className={`bg-slate-800/40 border ${isCompleted ? 'border-green-600/50 bg-green-900/10' : 'border-slate-700/50'} rounded-lg p-6 transition-all hover:border-slate-600/50`}
                  >
                    <div className="flex items-start gap-4">
                      <Checkbox 
                        checked={isCompleted} 
                        onCheckedChange={() => toggleComplete(item.id)} 
                        className="mt-1 border-slate-500 data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600" 
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <div className={`p-2 rounded-lg ${typeConfig.bg}`}>
                            <IconComponent className={`h-4 w-4 ${typeConfig.color}`} />
                          </div>
                          <span className={`text-xs uppercase tracking-wider ${typeConfig.color}`}>
                            {item.assignment_type}
                          </span>
                          {item.estimated_time && (
                            <span className="text-xs text-slate-500">• {item.estimated_time}</span>
                          )}
                        </div>
                        <p className={`text-lg ${isCompleted ? 'line-through text-slate-500' : 'text-white'}`}>
                          {item.title}
                        </p>
                        {item.description && (
                          <p className="text-slate-400 text-sm mt-1">{item.description}</p>
                        )}
                        {item.content && !isCompleted && (
                          <div className="mt-3 p-4 bg-slate-900/50 border border-slate-700/30 rounded-lg">
                            <p className="text-slate-300 text-sm whitespace-pre-wrap">{item.content}</p>
                          </div>
                        )}
                        {item.page_reference && (
                          <p className="text-slate-500 text-xs mt-2">📄 {item.page_reference}</p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Completion Celebration */}
          {progress === 100 && (
            <div className="mt-8 bg-gradient-to-r from-green-900/30 to-emerald-900/30 border border-green-500/30 rounded-2xl p-8 text-center">
              <span className="text-5xl mb-4 block">🎉</span>
              <h3 className="text-2xl font-bold text-green-300 mb-2">Congratulations!</h3>
              <p className="text-green-200">You've completed all Chapter 1 homework assignments!</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
